import { Trans } from "@lingui/macro";
import Link from "next/link";
import styled from "styled-components";
import "swiper/css";
import { Swiper, SwiperSlide } from "swiper/react";
import { LogeWithOffers } from "~/applications/OfferSearch/Domain/LogeWithOffers";
import StayRequest from "~/core/StayRequest";
import { offerSearchUnitGroupRoute } from "~/core/routes";
import { Button } from "~/guidelines/Button";
import mediaQueries from "~/guidelines/Theme/mediaQueries";
import { body2, heading5, heading7 } from "~/guidelines/Typography";
import { usePriceFormatter } from "~/guidelines/usePriceFormatter";

const Container = styled.div`
  background: ${({ theme }) => theme.color.background.lightFirst};
  border-radius: 0px 0px 8px 8px;
  padding: 40px 16px;
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;

  ${mediaQueries.xl()} {
    width: 40%;
    padding: 40px 32px;
    border-radius: 0px 8px 8px 0px;
  }
`;

const InformationWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const InformationTitle = styled.h4`
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
  ${heading5};
`;

const InformationText = styled.span<{ hideOnMobile?: boolean }>`
  ${body2};
  color: ${({ theme }) => theme.color.text.lightThird};

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    display: ${({ hideOnMobile }) => (hideOnMobile ? "none" : "block")};
  }
`;

const RoomInformationWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MobileSwiperRoomCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: left;
  gap: 2px;
  padding: 10px;
  border-radius: 8px;
  height: 100%;
  box-shadow:
    0px 4px 11px 0px rgba(104, 85, 39, 0.15),
    0px 9px 16px 0px rgba(104, 85, 39, 0.07);
`;

const StretchedLink = styled(Link)<{ animateHover?: boolean }>`
  text-decoration: none;
  color: inherit;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transition: background-color 150ms ease-in-out;
    border-radius: 8px;
  }

  ${({ animateHover }) =>
    animateHover &&
    `
    &:hover::after {
      background-color: rgba(0, 0, 0, 0.05);
    }
  `}
`;

const MobileRoomImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  border-radius: 6px;
  overflow: hidden;
  max-width: 80px;
`;

const MobileRoomTitle = styled.h5`
  ${heading7};
  text-align: center;
  padding: 0;
  margin: 8px 0 0 0;
  flex: 1;
`;

const MobileCapacityText = styled.span`
  @media (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    display: none;
  }
`;

const MobileRoomText = styled.span`
  ${body2};
  color: ${({ theme }) => theme.color.text.lightFirst};
  font-size: 10px;
`;

const MobileRoomPrice = styled.span`
  ${body2};
  color: ${({ theme }) => theme.color.text.lightTertiary};
  font-weight: 600;
  font-size: 12px;
  margin-top: auto;
`;

const MobileSwiperContainer = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    display: block;
    margin-bottom: 8px;
    overflow: hidden;
    width: calc(100vw - 70px);
    margin-left: -16px;
    padding-top: 16px;
    padding-bottom: 16px;
    padding-left: 16px;

    .swiper {
      padding: 0 16px 0 0;
      overflow: visible;
    }

    .swiper-slide {
      width: auto !important;
    }
  }
`;

const DesktopRoomList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;

  @media (max-width: ${({ theme }) => theme.breakpoints.xl}) {
    display: none;
  }
`;

const ButtonWrapper = styled.div`
  margin-top: auto;
`;

const RoomWrapper = styled.div`
  position: relative;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  border-radius: 8px;
`;

const RoomImage = styled.div`
  width: 63px;
  height: 50px;
  border-radius: 6px;
  overflow: hidden;
`;

const RoomInformationText = styled.span`
  ${body2};
  color: ${({ theme }) => theme.color.text.lightFirst};
  margin-top: auto;
`;

const RoomInformationTitle = styled.h5`
  ${heading7};
  padding: 0;
  margin: 0;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RoomPrice = styled.span`
  ${body2};
  color: ${({ theme }) => theme.color.text.lightTertiary};
  font-weight: 600;
`;

interface HotelRoomListProps {
  hotel: LogeWithOffers;
  selectedStayRequest: StayRequest;
  destinationSlug: string;
}

const HotelRoomList: React.FC<HotelRoomListProps> = ({
  hotel,
  selectedStayRequest,
  destinationSlug,
}) => {
  const { marketingPriceFormatter } = usePriceFormatter();

  const displayedOffers = hotel?.offers?.slice(0, 4);
  const numberOfNights = selectedStayRequest.getNbNight();

  return (
    <Container>
      <InformationTitle>
        <Trans>Hotel</Trans> {hotel.name}
      </InformationTitle>

      <InformationWrapper>
        <InformationText>
          <Trans>
            For {numberOfNights} night{numberOfNights > 1 ? "s" : ""}
          </Trans>
        </InformationText>

        <InformationText hideOnMobile>
          <Trans>starting from</Trans>
        </InformationText>
      </InformationWrapper>

      <RoomInformationWrapper>
        <DesktopRoomList>
          {displayedOffers.map((offer) => {
            const prices = offer.prices();

            const roomUrl = offerSearchUnitGroupRoute(
              destinationSlug,
              hotel.slug,
              offer.unitGroup.slug,
              selectedStayRequest,
            );

            return (
              <RoomWrapper key={offer.unitGroup.id}>
                <div style={{ display: "flex", gap: "8px" }}>
                  <RoomImage>
                    <img
                      src={offer.unitGroup.images[0].asset.url}
                      alt={offer.unitGroup.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </RoomImage>

                  <RoomInformationWrapper>
                    <StretchedLink href={roomUrl} passHref animateHover>
                      <RoomInformationTitle>
                        {offer.unitGroup.name}
                      </RoomInformationTitle>
                    </StretchedLink>

                    <RoomInformationText>
                      {selectedStayRequest.nbGuests}{" "}
                      {selectedStayRequest.nbGuests > 1
                        ? "personnes"
                        : "personne"}{" "}
                      • {offer.unitGroup.surface.minimum}-
                      {offer.unitGroup.surface.maximum} m²
                    </RoomInformationText>
                  </RoomInformationWrapper>
                </div>

                <RoomPrice>
                  {marketingPriceFormatter(prices.totalPrice)}
                </RoomPrice>
              </RoomWrapper>
            );
          })}
        </DesktopRoomList>

        {displayedOffers && (
          <MobileSwiperContainer>
            <Swiper
              spaceBetween={16}
              slidesPerView={"auto"}
              freeMode={true}
              style={{ width: "auto" }}
            >
              {displayedOffers.map((offer) => {
                const prices = offer.prices();
                const roomUrl = offerSearchUnitGroupRoute(
                  destinationSlug,
                  hotel.slug,
                  offer.unitGroup.slug,
                  selectedStayRequest,
                );

                return (
                  <SwiperSlide
                    key={offer.unitGroup.id}
                    style={{ maxWidth: "150px", height: "auto" }}
                  >
                    <MobileSwiperRoomCard>
                      <MobileRoomImage>
                        <img
                          src={offer.unitGroup.images[0].asset.url}
                          alt={offer.unitGroup.name}
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </MobileRoomImage>

                      <StretchedLink href={roomUrl} passHref>
                        <MobileRoomTitle>
                          {offer.unitGroup.name}
                        </MobileRoomTitle>
                      </StretchedLink>

                      <MobileRoomText>
                        {selectedStayRequest.nbGuests}{" "}
                        {selectedStayRequest.nbGuests > 1
                          ? "personnes"
                          : "personne"}{" "}
                        <MobileCapacityText>
                          • Capacité max {offer.unitGroup.maxCapacity}
                        </MobileCapacityText>
                      </MobileRoomText>
                      <MobileRoomPrice>
                        {marketingPriceFormatter(prices.totalPrice)}
                      </MobileRoomPrice>
                    </MobileSwiperRoomCard>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </MobileSwiperContainer>
        )}

        <ButtonWrapper>
          <Button
            size="large"
            kind={hotel.offers.length > 4 ? "secondary" : "primary"}
            fluid
            href={`/destinations/${destinationSlug}/${hotel.slug}`}
            variant="light_background"
          >
            {hotel.offers.length > 4 ? (
              <Trans>View all rooms</Trans>
            ) : (
              <Trans>View rates and availability</Trans>
            )}
          </Button>
        </ButtonWrapper>
      </RoomInformationWrapper>
    </Container>
  );
};

export default HotelRoomList;
